import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { CSVUploadModal } from './Master';
import Views from './SurveillanceViews';
import PowerLog from './PowerLog';

const KINDS = [
  { id:'dvr',      label:'DVR / NVR' },
  { id:'camera',   label:'CCTV Camera' },
  { id:'smps',     label:'CCTV SMPS' },
  { id:'harddisk', label:'Surveillance Hard Disk' },
];
const STATUS_COLOR = {
  Healthy: { bg:'#93C47D', fg:'#12300a' },
  Faulty:  { bg:'#FF9900', fg:'#3a2200' },
  Offline: { bg:'#E5484D', fg:'#ffffff' },
};
const REASONS = ['Recording missing','Playback corrupted','Hard disk failure','Power interruption','Unknown'];
const LIVE_REASONS = ['Camera offline','No power','Network / cable issue','Camera damaged','Blurred / poor image','Under maintenance','Other'];
// Recording is continuous everywhere, so retention isn't configured per DVR —
// playback is expected to reach back this many days.
const DEFAULT_RETENTION = 15;
const expectedDays = d => d?.expected_retention_days || DEFAULT_RETENTION;
// overall_status is a plain VARCHAR(20) with no CHECK constraint, so a value
// outside the three known ones can exist. Looking its colour up blindly and
// reading .bg off the miss is what blanked the whole tab — an unknown status
// is now shown as itself in neutral colours instead of crashing the page.
const pill = s => {
  if (!s) return <span style={{ fontSize:11, color:'var(--text3)' }}>Not checked</span>;
  const c = STATUS_COLOR[s] || { bg:'var(--bg2)', fg:'var(--text2)' };
  return <span style={{ background:c.bg, color:c.fg, fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:12 }}>{s}</span>;
};
const fmt = d => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'2-digit' }) : '—';

export default function Surveillance() {
  const { user } = useAuth();
  const canEdit = ['admin','it_head','it_staff','it_lead'].includes(user?.role);
  const [tab, setTab] = useState('health');
  return (
    <div>
      <div style={{ marginBottom:14 }}>
        <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>📹 Surveillance</h2>
        <p style={{ fontSize:13, color:'var(--text2)' }}>CCTV assets, daily DVR health checks and playback retention.</p>
      </div>
      <div className="flex-gap" style={{ gap:8, marginBottom:16, flexWrap:'wrap' }}>
        {[['health','🩺 Daily Health Check'],['devices','🎥 Devices'],['views','📷 Views'],['footage','🎞 Footage Register'],['dash','📊 Dashboard']].map(([id,l]) => (
          <button key={id} className={`btn btn-sm ${tab===id?'btn-primary':'btn-secondary'}`} onClick={() => setTab(id)}>{l}</button>
        ))}
      </div>
      {tab === 'health'  && <HealthCheck canEdit={canEdit} />}
      {tab === 'devices' && <Devices canEdit={canEdit} />}
      {tab === 'views'   && <Views canEdit={canEdit} />}
      {tab === 'footage' && <Footage canEdit={canEdit} />}
      {tab === 'dash'    && <Dashboard />}
    </div>
  );
}

// ─── Daily health check ─────────────────────────────────────────────────────
function HealthCheck({ canEdit }) {
  const [dvrs, setDvrs] = useState([]);
  const [open, setOpen] = useState(null);   // dvr being checked
  const [loading, setLoading] = useState(true);
  const [busyOk, setBusyOk] = useState(null);
  const [err, setErr] = useState('');

  // `first` only — a refresh must not replace the whole list with "Loading…".
  // It used to: if the refresh then stalled, the page sat on that word with no
  // error and nothing to click, which reads as the page having hung.
  const load = useCallback((first = false) => {
    if (first) setLoading(true);
    setErr('');
    return api.get('/surveillance/health/today', { timeout: 20000 })
      // A body that is not a list would throw at .map() below and blank the
      // page, so anything unexpected is treated as an empty list.
      .then(r => setDvrs(Array.isArray(r.data) ? r.data : []))
      // Swallowing this was the other half of the problem — a failed refresh
      // left the old list on screen with no sign anything had gone wrong.
      .catch(e => setErr(e.code === 'ECONNABORTED'
        ? 'The list took too long to load. Check your connection and try Refresh.'
        : (e.response?.data?.error || 'Could not refresh the list.')))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(true); }, [load]);

  // Records the same row the form would: powered on, every camera working,
  // playback fine. Confirmed when it would overwrite a check already done.
  const allOk = async (d) => {
    if (d.check_id && !window.confirm(`Today's check for ${d.dvr_name || d.asset_tag} is already recorded. Replace it with "all OK"?`)) return;
    setBusyOk(d.id); setErr('');
    try {
      // A timeout, so a request that never comes back reports instead of
      // leaving the button spinning for ever.
      const r = await api.post(`/surveillance/health/all-ok/${d.id}`, {}, { timeout: 20000 });
      // Checks are marked one DVR at a time, so only that card is replaced.
      // Reloading every DVR on each click is what made marking a campus feel
      // like the page had stopped responding.
      if (r.data?.row) setDvrs(list => list.map(x => x.id === d.id ? r.data.row : x));
      else await load();                      // older server: fall back
    } catch (e) {
      setErr(e.code === 'ECONNABORTED'
        ? 'That took too long and may not have been saved — press Refresh to see.'
        : (e.response?.data?.error || 'Could not record it.'));
    } finally { setBusyOk(null); }
  };

  if (loading) return <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>Loading…</div>;
  if (!dvrs.length) return <div className="card"><div className="empty-state"><p>No DVRs registered yet. Add them under <b>Devices</b>.</p></div></div>;

  const done = dvrs.filter(d => d.check_id).length;
  return (
    <div>
      <div className="flex-gap" style={{ fontSize:13, color:'var(--text2)', marginBottom:12,
                                         alignItems:'center', gap:10, flexWrap:'wrap' }}>
        <span><b>{done}</b> of <b>{dvrs.length}</b> DVRs checked today.</span>
        <button className="btn btn-secondary btn-sm" onClick={() => load()}>↻ Refresh</button>
        {err && <span style={{ color:'var(--red)' }}>{err}</span>}
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(330px, 1fr))', gap:14 }}>
        {dvrs.map(d => {
          const short = d.retention_days != null && d.retention_days < expectedDays(d);
          return (
            <div key={d.id} className="card" style={{ padding:16 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8, marginBottom:8 }}>
                <div>
                  <div style={{ fontWeight:600 }}>{d.dvr_name || d.asset_tag}</div>
                  <div style={{ fontSize:12, color:'var(--text2)' }}>{d.campus_name} · {d.camera_count} camera(s)</div>
                </div>
                {pill(d.overall_status)}
              </div>
              <div style={{ fontSize:12, color:'var(--text2)', lineHeight:1.7 }}>
                <div>Hard disk: <b style={{ color:'var(--text)' }}>{d.hdd_capacity_tb ? `${d.hdd_capacity_tb} TB` : '—'}</b></div>
                <div>Oldest recording: <b style={{ color:'var(--text)' }}>{fmt(d.oldest_recording_date)}</b></div>
                <div>Playback retention: <b style={{ color: short ? 'var(--orange)' : 'var(--text)' }}>
                  {d.retention_days != null ? `${d.retention_days} days` : '—'}</b>
                  <span> / {expectedDays(d)} expected</span>
                </div>
                <div>Last verified: <b style={{ color:'var(--text)' }}>{fmt(d.last_check)}</b>
                  {d.checked_by_name && <span> · {d.checked_by_name}</span>}</div>
              </div>
              {short && (
                <div style={{ marginTop:8, fontSize:12, background:'rgba(255,153,0,0.12)', border:'1px solid #FF9900',
                  borderRadius:6, padding:'6px 10px', color:'var(--orange)' }}>
                  🟠 Retention below expected
                </div>
              )}
              {canEdit && (
                <div className="flex-gap" style={{ gap:8, marginTop:10, flexWrap:'wrap' }}>
                  {/* The usual answer is "nothing wrong", so record that in one
                      click instead of opening the form. Still editable after. */}
                  <button className="btn btn-secondary btn-sm" disabled={busyOk === d.id}
                    onClick={() => allOk(d)}>
                    {busyOk === d.id ? '…' : '✓ All OK'}
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={() => setOpen(d)}>
                    {d.check_id ? 'Update today’s check' : 'Do today’s check'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {open && <CheckModal dvr={open} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
    </div>
  );
}

const todayStr = () => new Date().toISOString().slice(0,10);
function CheckModal({ dvr, onClose, onSaved }) {
  const [powered, setPowered] = useState(dvr.dvr_powered_on ?? true);
  // Per-camera row state — live status (+reason) and playback (oldest date +reason)
  const [cams, setCams] = useState(dvr.cameras.map(c => ({
    id: c.id, port_number: c.port_number, camera_number: c.camera_number,
    camera_name: c.camera_name, asset_tag: c.asset_tag, location_name: c.location_name,
    live_ok: c.live_ok !== false,
    live_reason: c.live_reason || '',
    playback_ok: c.playback_ok !== false,
    playback_oldest_date: c.playback_oldest_date ? c.playback_oldest_date.slice(0,10) : '',
    playback_reason: c.playback_reason || '',
  })));
  const [remarks, setRemarks] = useState(dvr.remarks || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [logFor, setLogFor] = useState(null);      // camera id whose log is open
  const [log, setLog] = useState([]);

  const openLog = async c => {
    if (logFor === c.id) { setLogFor(null); return; }
    setLogFor(c.id); setLog([]);
    try { const r = await api.get(`/surveillance/health/camera-log/${c.id}`); setLog(r.data); } catch (e) {}
  };

  const setCam = (i, patch) => setCams(cs => cs.map((x, j) => j===i ? { ...x, ...patch } : x));
  const markAllHealthy = () => setCams(cs => cs.map(c => ({ ...c, live_ok:true, live_reason:'', playback_ok:true, playback_reason:'' })));
  const liveUp = cams.filter(c => c.live_ok).length;
  const pbIssues = cams.filter(c => !c.playback_ok).length;
  // Oldest recording across cameras → retention
  const oldestDates = cams.map(c => c.playback_oldest_date).filter(Boolean).sort();
  const oldest = oldestDates[0] || '';
  const retention = oldest ? Math.max(0, Math.floor((Date.now() - new Date(oldest)) / 86400000)) : null;

  const save = async () => {
    for (const c of cams) {
      const nm = c.camera_name || c.asset_tag;
      if (!c.live_ok && !c.live_reason) { setErr(`Select a live-status reason for ${nm}`); return; }
      if (!c.playback_ok && !c.playback_reason) { setErr(`Select a playback reason for ${nm}`); return; }
    }
    setBusy(true); setErr('');
    try {
      await api.post(`/surveillance/health/${dvr.id}`, {
        dvr_powered_on: powered, remarks,
        cameras: cams.map(c => ({
          camera_asset_id: c.id, camera_name: c.camera_name,
          live_ok: c.live_ok, live_reason: c.live_ok ? null : c.live_reason,
          playback_ok: c.playback_ok, playback_reason: c.playback_ok ? null : c.playback_reason,
          playback_oldest_date: c.playback_oldest_date || null,
        })),
      });
      onSaved();
    } catch (e) { setErr(e.response?.data?.error || 'Could not save'); } finally { setBusy(false); }
  };

  const cell = { padding:'6px 8px', fontSize:12, borderBottom:'1px solid var(--border)', verticalAlign:'top', wordBreak:'break-word' };
  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && onClose()}>
      <div className="modal" style={{ maxWidth:860, maxHeight:'92vh', overflowY:'auto' }}>
        <div className="modal-header">
          <span className="modal-title">Health check — {dvr.dvr_name || dvr.asset_tag}</span>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:6 }}>STEP 1 — DVR STATUS</div>
        <label className="opt" style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, marginBottom:16 }}>
          <input type="checkbox" checked={powered} onChange={e => setPowered(e.target.checked)} /> DVR Powered ON
        </label>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6, flexWrap:'wrap', gap:8 }}>
          <span style={{ fontSize:12, fontWeight:600, color:'var(--text2)' }}>
            STEP 2 — CAMERAS · live {liveUp}/{cams.length}{pbIssues>0 && ` · ${pbIssues} playback issue(s)`}
          </span>
          {cams.length > 0 && (
            <button className="btn btn-secondary btn-sm" onClick={markAllHealthy}>Mark All Cameras Healthy</button>
          )}
        </div>

        {cams.length === 0 ? (
          <div style={{ fontSize:12, color:'var(--text2)', marginBottom:14 }}>No cameras linked to this DVR yet. Add them under Devices.</div>
        ) : (
          <div style={{ overflowX:'auto', border:'1px solid var(--border)', borderRadius:'var(--radius)', marginBottom:14 }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:640, tableLayout:'fixed' }}>
              <colgroup>
                <col style={{ width:60 }} />
                <col />
                <col style={{ width:130 }} />
                <col style={{ width:250 }} />
              </colgroup>
              <thead>
                <tr style={{ textAlign:'left', background:'var(--bg3)' }}>
                  <th style={cell}>Port</th>
                  <th style={cell}>Camera Name</th>
                  <th style={cell}>Live Status</th>
                  <th style={cell}>Playback</th>
                </tr>
              </thead>
              <tbody>
                {cams.map((c, i) => (
                  <tr key={c.id}>
                    <td style={{ ...cell }} className="mono">{c.port_number || c.camera_number || '—'}</td>
                    <td style={cell}>
                      <div style={{ fontWeight:500 }}>{c.camera_name || c.asset_tag}
                        <button onClick={() => openLog(c)} title="Status log"
                          style={{ marginLeft:6, background:'none', border:'none', cursor:'pointer', fontSize:12, color:'var(--accent)' }}>📜</button>
                      </div>
                      {c.location_name && <div style={{ color:'var(--text2)', fontSize:11 }}>{c.location_name}</div>}
                      {logFor === c.id && (
                        <div style={{ marginTop:4, padding:'4px 6px', background:'var(--bg3)', borderRadius:6, maxHeight:120, overflowY:'auto' }}>
                          {!log.length ? <div style={{ fontSize:11, color:'var(--text2)' }}>No past checks.</div>
                            : log.map((l, k) => (
                              <div key={k} style={{ fontSize:11, color:'var(--text2)', lineHeight:1.5 }}>
                                {fmt(l.check_date)} · live <b style={{ color: l.live_ok?'var(--green)':'var(--red)' }}>{l.live_ok?'OK':'Not OK'}</b>
                                {l.live_reason && ` (${l.live_reason})`} · pb <b style={{ color: l.playback_ok===false?'var(--red)':'var(--green)' }}>{l.playback_ok===false?'Issue':'OK'}</b>
                                {l.playback_reason && ` (${l.playback_reason})`}
                              </div>
                            ))}
                        </div>
                      )}
                    </td>
                    <td style={cell}>
                      <div className="flex-gap" style={{ gap:4, marginBottom:c.live_ok?0:4 }}>
                        <button className={`btn btn-sm ${c.live_ok?'btn-primary':'btn-secondary'}`}
                          style={c.live_ok?{ background:'var(--green)', borderColor:'var(--green)' }:{}}
                          onClick={() => setCam(i, { live_ok:true, live_reason:'' })}>OK</button>
                        <button className={`btn btn-sm ${!c.live_ok?'btn-danger':'btn-secondary'}`}
                          onClick={() => setCam(i, { live_ok:false })}>Not OK</button>
                      </div>
                      {!c.live_ok && (
                        <select value={c.live_reason} onChange={e => setCam(i, { live_reason:e.target.value })} style={{ fontSize:12, padding:'3px 4px', width:'100%' }}>
                          <option value="">— Reason —</option>{LIVE_REASONS.map(r => <option key={r}>{r}</option>)}
                        </select>
                      )}
                    </td>
                    <td style={cell}>
                      <input type="date" max={todayStr()} value={c.playback_oldest_date}
                        onChange={e => setCam(i, { playback_oldest_date:e.target.value })}
                        title="Oldest playback date" style={{ fontSize:12, padding:'2px 4px', width:'100%', marginBottom:4, boxSizing:'border-box' }} />
                      <div className="flex-gap" style={{ gap:4, marginBottom:4 }}>
                        <button className={`btn btn-sm ${c.playback_ok?'btn-primary':'btn-secondary'}`}
                          style={c.playback_ok?{ background:'var(--green)', borderColor:'var(--green)' }:{}}
                          onClick={() => setCam(i, { playback_ok:true, playback_reason:'' })}>OK</button>
                        <button className={`btn btn-sm ${!c.playback_ok?'btn-danger':'btn-secondary'}`}
                          onClick={() => setCam(i, { playback_ok:false })}>Issue</button>
                      </div>
                      {!c.playback_ok && (
                        <select value={c.playback_reason} onChange={e => setCam(i, { playback_reason:e.target.value })} style={{ fontSize:12, padding:'3px 4px', width:'100%' }}>
                          <option value="">— Reason —</option>{REASONS.map(r => <option key={r}>{r}</option>)}
                        </select>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {oldest && (
          <div style={{ fontSize:12, marginBottom:12, color: retention < expectedDays(dvr) ? 'var(--orange)' : 'var(--green)' }}>
            Oldest recording across cameras: <b>{fmt(oldest)}</b> · playback retention <b>{retention} days</b> (expected {expectedDays(dvr)}) — calculated automatically
          </div>
        )}

        {/* Power cuts are recorded here, with the day's check, because they
            are the explanation for a gap in the recording — not a separate
            errand to remember afterwards. Saved as they are entered, so they
            survive even if the check itself is abandoned. */}
        <PowerLog canEdit embedded campusId={dvr.campus_id} campusName={dvr.campus_name}
                  dvrs={[dvr]} date={todayStr()} />

        <div className="form-group"><label>Remarks</label>
          <textarea rows={2} value={remarks} onChange={e => setRemarks(e.target.value)} /></div>

        {err && <div className="err-msg">{err}</div>}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Saving…':'Save health check'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Devices ────────────────────────────────────────────────────────────────
const blankDev = { device_kind:'camera', campus_id:'', brand:'', model:'', serial_number:'', purchase_date:'',
  warranty_expiry:'', building:'', floor:'', location_id:'', room_id:'', remarks:'',
  camera_number:'', port_number:'', camera_name:'', camera_type:'', dvr_asset_id:'', viewing_direction:'', coverage_desc:'',
  ip_address:'', stream_url:'', dvr_name:'', channels:'', hdd_make:'', hdd_model:'', hdd_capacity_tb:'',
  hdd_serial:'', recording_days:'', firmware:'', recording_start_date:'', active_cameras:'',
  recording_mode:'', resolution:'', fps:'', compression:'', expected_retention_days:'' };

function Devices({ canEdit }) {
  const { user } = useAuth();
  const seesAll = ['admin','it_head','management'].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [kind, setKind] = useState('');
  const [campuses, setCampuses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [dvrs, setDvrs] = useState([]);
  const [modal, setModal] = useState(null);
  const [csv, setCsv] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    api.get('/surveillance/devices', { params: kind ? { kind } : {} }).then(r => setRows(Array.isArray(r.data) ? r.data : [])).catch(()=>{});
  }, [kind]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/master/campuses').then(r => setCampuses(r.data.filter(c=>c.active))).catch(()=>{});
    api.get('/surveillance/devices', { params:{ kind:'dvr' } }).then(r => setDvrs(Array.isArray(r.data) ? r.data : [])).catch(()=>{});
  }, []);
  useEffect(() => {
    if (modal?.campus_id) {
      api.get(`/master/locations/${modal.campus_id}`).then(r => setLocations(r.data.filter(l=>l.active))).catch(()=>setLocations([]));
      api.get(`/master/rooms/${modal.campus_id}`).then(r => setRooms(r.data.filter(rr=>rr.active))).catch(()=>setRooms([]));
    }
  }, [modal?.campus_id]);

  const openNew = () => { setModal({ ...blankDev, campus_id: seesAll ? '' : String(user.campus_ids?.[0] || '') }); setErr(''); };
  const openEdit = d => setModal({
    ...blankDev, ...d, id:d.id, campus_id:String(d.campus_id || ''),
    location_id: d.location_id ? String(d.location_id) : '', room_id: d.room_id ? String(d.room_id) : '',
    dvr_asset_id: d.dvr_asset_id ? String(d.dvr_asset_id) : '',
    purchase_date: d.purchase_date ? d.purchase_date.slice(0,10) : '',
    warranty_expiry: d.warranty_expiry ? d.warranty_expiry.slice(0,10) : '',
    recording_start_date: d.recording_start_date ? d.recording_start_date.slice(0,10) : '',
  });
  const save = async () => {
    if (!modal.campus_id) { setErr('Campus is required'); return; }
    setBusy(true); setErr('');
    try {
      if (modal.id) await api.put(`/surveillance/devices/${modal.id}`, modal);
      else await api.post('/surveillance/devices', { ...modal, campus_id: parseInt(modal.campus_id) });
      setModal(null); load();
    } catch (e) { setErr(e.response?.data?.error || 'Save failed'); } finally { setBusy(false); }
  };

  const isCam = modal?.device_kind === 'camera';
  const isDvr = modal?.device_kind === 'dvr';

  return (
    <div>
      <div className="flex-gap" style={{ gap:8, marginBottom:12, justifyContent:'space-between', flexWrap:'wrap' }}>
        <select value={kind} onChange={e => setKind(e.target.value)} style={{ padding:'6px 8px', minWidth:190 }}>
          <option value="">All device types</option>
          {KINDS.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
        </select>
        {canEdit && (
          <div className="flex-gap" style={{ gap:8 }}>
            <button className="btn btn-secondary" onClick={() => setCsv(true)}>⬆ Bulk Upload</button>
            <button className="btn btn-primary" onClick={openNew}>+ Add Device</button>
          </div>
        )}
      </div>

      {csv && (
        <CSVUploadModal
          title="Bulk Upload Surveillance Devices"
          sampleFile={{ name:'surveillance-devices-sample.csv',
            content: 'Device Type,Campus,DVR Name,Camera Name,Connected DVR,DVR Channel,Audio,Asset Tag,Brand,Model,Serial Number,Room,Block,Floor,Camera Number,Camera Type,IP Address,Channels,Remarks\n'
              + 'DVR,CBE-MAIN,DVR-01,,,,,,Hikvision,DS-7216,,Server Room,Admin Block,Ground,,,192.168.1.10,16,\n'
              + 'Camera,CBE-MAIN,,Main Gate Entry,DVR-01,1,Yes,,Hikvision,DS-2CE,,101,Admin Block,Ground,1,Bullet,,,\n'
              + 'Camera,CBE-MAIN,,Reception,DVR-01,2,No,,Hikvision,DS-2CE,,Reception,Admin Block,Ground,2,Dome,,,\n' }}
          formatHint={
            <>
              <strong>Required:</strong> Device Type, Campus. Everything else can be left blank and
              filled in later — serial numbers especially.<br />
              <strong>Device Type:</strong> DVR / Camera / SMPS / Hard Disk. <strong>Campus</strong> = code or name.<br />
              <strong>Connected DVR</strong> (cameras) = the DVR Name or asset tag. DVRs in the same
              file are handled first, so one sheet with DVRs and their cameras works.<br />
              <strong>Audio:</strong> Yes / No, or blank if you do not know yet.<br />
              <strong>Uploading again updates</strong> rather than duplicating — a DVR is matched on
              campus + DVR Name, a camera on campus + Connected DVR + DVR Channel (falling back to
              Camera Number). Give an <strong>Asset Tag</strong> to target one exact device.
              Columns you leave out of the file are not touched, so a sheet without Serial Number
              will not wipe the serials already recorded.
            </>
          }
          columns={[{key:'device_kind',label:'Type'},{key:'campus',label:'Campus'},{key:'camera_name',label:'Camera'},
            {key:'dvr_name',label:'DVR'},{key:'linked_dvr',label:'Linked DVR'},{key:'serial_number',label:'Serial'}]}
          mapRow={r => {
            const pick = (...k) => { for (const x of k) { const v = r[x]; if (v && String(v).trim()) return String(v).trim(); } return ''; };
            const device_kind = pick('device_type','device_kind','type');
            const campus = pick('campus','campus_code','site');
            if (!device_kind || !campus) return null;
            return { device_kind, campus,
              asset_tag: pick('asset_tag','tag'),
              has_audio: pick('audio','has_audio','audio_option'),
              brand: pick('brand'), model: pick('model'), serial_number: pick('serial_number','serial'),
              location: pick('location'), room: pick('room','room_number','room_no'), building: pick('building','block'), floor: pick('floor'),
              remarks: pick('remarks'),
              camera_number: pick('camera_number','camera_no'), port_number: pick('dvr_channel','port_number','port','channel'), camera_name: pick('camera_name'),
              camera_type: pick('camera_type'), linked_dvr: pick('connected_dvr','linked_dvr','dvr'),
              viewing_direction: pick('viewing_direction','direction'), coverage_desc: pick('coverage_description','coverage_desc'),
              ip_address: pick('ip_address','ip'), dvr_name: pick('dvr_name'), channels: pick('channels'),
              hdd_make: pick('hdd_make','hard_disk_make'), hdd_model: pick('hdd_model'),
              hdd_capacity_tb: pick('hdd_capacity_tb','capacity_tb','hdd_capacity'), hdd_serial: pick('hdd_serial'),
              firmware: pick('firmware'), resolution: pick('resolution'), compression: pick('compression'),
              purchase_date: pick('purchase_date'), warranty_expiry: pick('warranty_expiry','warranty'),
            };
          }}
          onUpload={rws => api.post('/surveillance/devices/import', { records: rws }).then(r => r.data)}
          onDone={load}
          onClose={() => setCsv(false)}
        />
      )}

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!rows.length ? <div className="empty-state"><p>No surveillance devices yet.</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr>
              <th>Asset ID</th><th>Type</th><th>Name / Number</th><th>Campus</th><th>Room</th>
              <th>Linked DVR</th><th>Serial</th>{canEdit && <th></th>}
            </tr></thead>
            <tbody>{rows.map(d => (
              <tr key={d.id}>
                <td className="mono" style={{ fontSize:12, color:'var(--accent)' }}>{d.asset_tag}</td>
                <td style={{ fontSize:12 }}>{KINDS.find(k=>k.id===d.device_kind)?.label || d.device_kind}</td>
                <td style={{ fontSize:13, fontWeight:500 }}>
                  {d.device_kind==='camera' ? `${d.camera_number ? '#'+d.camera_number+' · ' : ''}${d.camera_name || '—'}`
                    : d.device_kind==='dvr' ? `${d.dvr_name || '—'}${d.channels ? ` (${d.channels}ch)` : ''}`
                    : `${d.brand || ''} ${d.model || ''}`.trim() || '—'}
                  {d.device_kind==='dvr' && <div style={{ fontSize:11, color:'var(--text2)' }}>{d.linked_cameras} camera(s)</div>}
                </td>
                <td style={{ fontSize:12 }}>{d.campus_name}</td>
                <td style={{ fontSize:12, color:'var(--text2)' }}>{d.room_name || d.location_name || '—'}</td>
                <td style={{ fontSize:12, color:'var(--text2)' }}>{d.dvr_name_linked || d.dvr_tag || '—'}</td>
                <td className="mono" style={{ fontSize:11 }}>{d.serial_number || '—'}</td>
                {canEdit && <td><button className="btn btn-secondary btn-sm" onClick={() => openEdit(d)}>Edit</button></td>}
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setModal(null)}>
          <div className="modal" style={{ maxWidth:700, maxHeight:'92vh', overflowY:'auto' }}>
            <div className="modal-header">
              <span className="modal-title">{modal.id ? 'Edit Device' : 'Add Surveillance Device'}</span>
              <button className="modal-close" onClick={() => setModal(null)}>×</button>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Device Type *</label>
                <select value={modal.device_kind} disabled={!!modal.id}
                  onChange={e => setModal(m => ({ ...m, device_kind:e.target.value }))}>
                  {KINDS.map(k => <option key={k.id} value={k.id}>{k.label}</option>)}
                </select></div>
              <div className="form-group"><label>Campus *</label>
                <select value={modal.campus_id} disabled={!!modal.id}
                  onChange={e => setModal(m => ({ ...m, campus_id:e.target.value, location_id:'' }))}>
                  <option value="">— Select —</option>
                  {campuses.filter(c => seesAll || (user.campus_ids||[]).includes(c.id)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Brand</label>
                <input value={modal.brand} onChange={e => setModal(m => ({ ...m, brand:e.target.value }))} /></div>
              <div className="form-group"><label>Model</label>
                <input value={modal.model} onChange={e => setModal(m => ({ ...m, model:e.target.value }))} /></div>
              <div className="form-group"><label>Serial Number</label>
                <input value={modal.serial_number} onChange={e => setModal(m => ({ ...m, serial_number:e.target.value }))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Room</label>
                <select value={modal.room_id} onChange={e => setModal(m => ({ ...m, room_id:e.target.value }))}>
                  <option value="">— Select room —</option>
                  {rooms.map(r => <option key={r.id} value={r.id}>{r.name}{r.code ? ` (${r.code})` : ''}</option>)}
                </select></div>
              <div className="form-group"><label>Block / Building</label>
                <input value={modal.building} onChange={e => setModal(m => ({ ...m, building:e.target.value }))} /></div>
              <div className="form-group"><label>Floor</label>
                <input value={modal.floor} onChange={e => setModal(m => ({ ...m, floor:e.target.value }))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Purchase Date</label>
                <input type="date" value={modal.purchase_date} onChange={e => setModal(m => ({ ...m, purchase_date:e.target.value }))} /></div>
              <div className="form-group"><label>Warranty Expiry</label>
                <input type="date" value={modal.warranty_expiry} onChange={e => setModal(m => ({ ...m, warranty_expiry:e.target.value }))} /></div>
            </div>

            {isCam && (<>
              <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', margin:'6px 0' }}>CAMERA DETAILS</div>
              <div className="form-row">
                <div className="form-group"><label>Camera Number</label>
                  <input value={modal.camera_number} onChange={e => setModal(m => ({ ...m, camera_number:e.target.value }))} /></div>
                <div className="form-group"><label>Port / Channel No.</label>
                  <input value={modal.port_number} onChange={e => setModal(m => ({ ...m, port_number:e.target.value }))} placeholder="DVR port the camera is wired to" /></div>
                <div className="form-group"><label>Camera Name</label>
                  <input value={modal.camera_name} onChange={e => setModal(m => ({ ...m, camera_name:e.target.value }))} /></div>
                <div className="form-group"><label>Camera Type</label>
                  <input value={modal.camera_type} onChange={e => setModal(m => ({ ...m, camera_type:e.target.value }))} placeholder="Dome / Bullet / PTZ" /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Linked DVR</label>
                  <select value={modal.dvr_asset_id} onChange={e => setModal(m => ({ ...m, dvr_asset_id:e.target.value }))}>
                    <option value="">— None —</option>
                    {dvrs.filter(d => !modal.campus_id || String(d.campus_id) === String(modal.campus_id))
                         .map(d => <option key={d.id} value={d.id}>{d.dvr_name || d.asset_tag}</option>)}
                  </select></div>
                <div className="form-group"><label>Viewing Direction</label>
                  <input value={modal.viewing_direction} onChange={e => setModal(m => ({ ...m, viewing_direction:e.target.value }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>IP Address</label>
                  <input value={modal.ip_address} onChange={e => setModal(m => ({ ...m, ip_address:e.target.value }))} /></div>
                <div className="form-group"><label>Stream URL</label>
                  <input value={modal.stream_url} onChange={e => setModal(m => ({ ...m, stream_url:e.target.value }))} /></div>
              </div>
              <div className="form-group"><label>Coverage Description</label>
                <textarea rows={2} value={modal.coverage_desc} onChange={e => setModal(m => ({ ...m, coverage_desc:e.target.value }))} /></div>
            </>)}

            {isDvr && (<>
              <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', margin:'6px 0' }}>DVR DETAILS</div>
              <div className="form-row">
                <div className="form-group"><label>DVR Name</label>
                  <input value={modal.dvr_name} onChange={e => setModal(m => ({ ...m, dvr_name:e.target.value }))} placeholder="e.g. DVR-01" /></div>
                <div className="form-group"><label>Channels</label>
                  <input type="number" value={modal.channels} onChange={e => setModal(m => ({ ...m, channels:e.target.value }))} /></div>
                <div className="form-group"><label>DVR IP Address</label>
                  <input value={modal.ip_address} onChange={e => setModal(m => ({ ...m, ip_address:e.target.value }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Hard Disk Make</label>
                  <input value={modal.hdd_make} onChange={e => setModal(m => ({ ...m, hdd_make:e.target.value }))} /></div>
                <div className="form-group"><label>HDD Model</label>
                  <input value={modal.hdd_model} onChange={e => setModal(m => ({ ...m, hdd_model:e.target.value }))} /></div>
                <div className="form-group"><label>HDD Serial</label>
                  <input value={modal.hdd_serial} onChange={e => setModal(m => ({ ...m, hdd_serial:e.target.value }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Capacity (TB)</label>
                  <input type="number" step="0.5" value={modal.hdd_capacity_tb} onChange={e => setModal(m => ({ ...m, hdd_capacity_tb:e.target.value }))} /></div>
                <div className="form-group"><label>Firmware</label>
                  <input value={modal.firmware} onChange={e => setModal(m => ({ ...m, firmware:e.target.value }))} /></div>
              </div>

              <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', margin:'10px 0 6px' }}>
                RECORDING <span style={{ fontWeight:400, textTransform:'none' }}>— continuous; expected playback retention is {DEFAULT_RETENTION} days</span>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Recording Start Date</label>
                  <input type="date" value={modal.recording_start_date} onChange={e => setModal(m => ({ ...m, recording_start_date:e.target.value }))} /></div>
                <div className="form-group"><label>Resolution</label>
                  <input value={modal.resolution} onChange={e => setModal(m => ({ ...m, resolution:e.target.value }))} placeholder="e.g. 2MP / 4MP" /></div>
                <div className="form-group"><label>Compression</label>
                  <select value={modal.compression} onChange={e => setModal(m => ({ ...m, compression:e.target.value }))}>
                    <option value="">—</option><option>H.264</option><option>H.265</option><option>H.265+</option>
                  </select></div>
              </div>
            </>)}

            <div className="form-group"><label>Remarks</label>
              <textarea rows={2} value={modal.remarks} onChange={e => setModal(m => ({ ...m, remarks:e.target.value }))} /></div>
            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Saving…':'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Footage Verification Register ──────────────────────────────────────────
const NO_COVER = ['Blind Spot','Camera Facing Different Direction','Camera Not Installed',
  'Camera Under Maintenance','Camera Not Recording','Other'];
const FR_STATUS = ['Open','Verified','Footage Shared','Closed'];
const FR_COLOR = { Open:{bg:'#4285F4',fg:'#fff'}, Verified:{bg:'#FFD966',fg:'#3d2f00'},
  'Footage Shared':{bg:'#93C47D',fg:'#12300a'}, Closed:{bg:'#6AA84F',fg:'#fff'} };
const blankReq = { request_date:new Date().toISOString().slice(0,10), requested_by:'', department:'',
  email_ref:'', incident_date:'', incident_time:'', campus_id:'', building:'', location_id:'', purpose:'',
  involves_staff:false, involves_student:false, involves_outside:false, outside_details:'',
  camera_covers:null, primary_camera_id:'', no_coverage_reason:'', footage_available:null,
  recording_quality:'', verification_date:'', status:'Open', remarks:'',
  staff_ids:[], student_ids:[], camera_ids:[] };

function Footage({ canEdit }) {
  const { user } = useAuth();
  const seesAll = ['admin','it_head','management'].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [f, setF] = useState({ status:'', search:'' });
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // lookups for the open request's campus
  const [locations, setLocations] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [studentList, setStudentList] = useState([]);
  const [staffQ, setStaffQ] = useState(''); const [studentQ, setStudentQ] = useState('');

  const load = useCallback(() => {
    const params = {}; Object.entries(f).forEach(([k,v]) => { if (v) params[k]=v; });
    api.get('/surveillance/footage', { params }).then(r => setRows(Array.isArray(r.data) ? r.data : [])).catch(()=>{});
  }, [f]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/master/campuses').then(r => setCampuses(r.data.filter(c=>c.active))).catch(()=>{}); }, []);

  // Load campus-specific pickers whenever the request's campus changes
  useEffect(() => {
    const cid = modal?.campus_id;
    if (!cid) { setLocations([]); setCameras([]); setStaffList([]); setStudentList([]); return; }
    api.get(`/master/locations/${cid}`).then(r => setLocations(r.data.filter(l=>l.active))).catch(()=>setLocations([]));
    api.get('/surveillance/devices', { params:{ kind:'camera', campus_id:cid } }).then(r => setCameras(r.data)).catch(()=>setCameras([]));
    api.get(`/master/informedby/${cid}`).then(r => setStaffList(r.data.filter(s=>s.active))).catch(()=>setStaffList([]));
    api.get(`/master/students/${cid}`).then(r => setStudentList(r.data.filter(s=>s.active))).catch(()=>setStudentList([]));
  }, [modal?.campus_id]);

  const openNew = () => { setModal({ ...blankReq, campus_id: seesAll ? '' : String(user.campus_ids?.[0] || '') }); setErr(''); setStaffQ(''); setStudentQ(''); };
  const openEdit = async r => {
    try {
      const d = (await api.get(`/surveillance/footage/${r.id}`)).data;
      setModal({ ...blankReq, ...d, campus_id:String(d.campus_id||''),
        location_id: d.location_id ? String(d.location_id) : '',
        primary_camera_id: d.primary_camera_id ? String(d.primary_camera_id) : '',
        request_date: d.request_date?.slice(0,10) || '', incident_date: d.incident_date?.slice(0,10) || '',
        verification_date: d.verification_date?.slice(0,10) || '',
        staff_ids: d.staff.map(s=>s.id), student_ids: d.students.map(s=>s.id), camera_ids: d.cameras.map(c=>c.id),
      });
      setErr(''); setStaffQ(''); setStudentQ('');
    } catch (e) { window.alert('Could not open the request'); }
  };
  const save = async () => {
    if (!modal.campus_id) { setErr('Campus is required'); return; }
    if (!modal.requested_by.trim()) { setErr('Requested By is required'); return; }
    if (modal.camera_covers === false && !modal.no_coverage_reason) { setErr('Reason is required when no camera covers the incident'); return; }
    setBusy(true); setErr('');
    const body = { ...modal, campus_id: parseInt(modal.campus_id),
      location_id: modal.location_id ? parseInt(modal.location_id) : null,
      primary_camera_id: modal.primary_camera_id ? parseInt(modal.primary_camera_id) : null };
    try {
      if (modal.id) await api.put(`/surveillance/footage/${modal.id}`, body);
      else await api.post('/surveillance/footage', body);
      setModal(null); load();
    } catch (e) { setErr(e.response?.data?.error || 'Save failed'); } finally { setBusy(false); }
  };
  const toggle = (key, id) => setModal(m => ({ ...m,
    [key]: m[key].includes(id) ? m[key].filter(x => x !== id) : [...m[key], id] }));

  const statusPill = s => {
    const c = FR_COLOR[s] || FR_COLOR.Open;
    return <span style={{ background:c.bg, color:c.fg, fontSize:11, fontWeight:600, padding:'3px 10px', borderRadius:12, whiteSpace:'nowrap' }}>{s}</span>;
  };

  return (
    <div>
      <div className="flex-gap" style={{ gap:8, marginBottom:12, justifyContent:'space-between', flexWrap:'wrap' }}>
        <div className="flex-gap" style={{ gap:8, flexWrap:'wrap' }}>
          <input placeholder="Search requester / purpose / ref…" value={f.search}
            onChange={e => setF(s => ({ ...s, search:e.target.value }))} style={{ padding:'6px 8px', minWidth:220 }} />
          <select value={f.status} onChange={e => setF(s => ({ ...s, status:e.target.value }))} style={{ padding:'6px 8px' }}>
            <option value="">All statuses</option>{FR_STATUS.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        {canEdit && <button className="btn btn-primary" onClick={openNew}>+ New Request</button>}
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!rows.length ? <div className="empty-state"><p>No footage requests recorded yet.</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr>
              <th>Requested</th><th>Requested By</th><th>Incident</th><th>Campus · Location</th>
              <th>People</th><th>Coverage</th><th>Footage</th><th>Status</th>{canEdit && <th></th>}
            </tr></thead>
            <tbody>{rows.map(r => (
              <tr key={r.id}>
                <td style={{ fontSize:12, whiteSpace:'nowrap' }}>{fmt(r.request_date)}
                  {r.email_ref && <div style={{ fontSize:11, color:'var(--text2)' }}>{r.email_ref}</div>}</td>
                <td style={{ fontSize:13 }}>{r.requested_by}
                  {r.department && <div style={{ fontSize:11, color:'var(--text2)' }}>{r.department}</div>}</td>
                <td style={{ fontSize:12 }}>{fmt(r.incident_date)}{r.incident_time ? ` ${r.incident_time}` : ''}</td>
                <td style={{ fontSize:12 }}>{r.campus_name}
                  {r.location_name && <div style={{ fontSize:11, color:'var(--text2)' }}>{r.location_name}</div>}</td>
                <td style={{ fontSize:11, color:'var(--text2)' }}>
                  {[r.staff_count ? `${r.staff_count} staff` : null,
                    r.student_count ? `${r.student_count} student` : null,
                    r.involves_outside ? 'outside' : null].filter(Boolean).join(', ') || '—'}
                </td>
                <td style={{ fontSize:12 }}>
                  {r.camera_covers === true ? <span style={{ color:'var(--green)' }}>✓ Covered</span>
                    : r.camera_covers === false ? <span style={{ color:'var(--red)' }} title={r.no_coverage_reason}>✗ {r.no_coverage_reason || 'No'}</span>
                    : '—'}
                </td>
                <td style={{ fontSize:12 }}>
                  {r.footage_available === true ? (r.recording_quality || 'Available')
                    : r.footage_available === false ? <span style={{ color:'var(--red)' }}>Not available</span> : '—'}
                </td>
                <td>{statusPill(r.status)}</td>
                {canEdit && <td><button className="btn btn-secondary btn-sm" onClick={() => openEdit(r)}>Open</button></td>}
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target===e.currentTarget && false && setModal(null)}>
          <div className="modal" style={{ maxWidth:760, maxHeight:'92vh', overflowY:'auto' }}>
            <div className="modal-header">
              <span className="modal-title">{modal.id ? 'Footage Request' : 'New Footage Request'}</span>
              <button className="modal-close" onClick={() => setModal(null)}>×</button>
            </div>

            <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:6 }}>REQUEST</div>
            <div className="form-row">
              <div className="form-group"><label>Request Date</label>
                <input type="date" value={modal.request_date} onChange={e => setModal(m => ({ ...m, request_date:e.target.value }))} /></div>
              <div className="form-group"><label>Requested By *</label>
                <input value={modal.requested_by} onChange={e => setModal(m => ({ ...m, requested_by:e.target.value }))} /></div>
              <div className="form-group"><label>Department</label>
                <input value={modal.department} onChange={e => setModal(m => ({ ...m, department:e.target.value }))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Email Reference</label>
                <input value={modal.email_ref} onChange={e => setModal(m => ({ ...m, email_ref:e.target.value }))} placeholder="optional" /></div>
              <div className="form-group"><label>Incident Date</label>
                <input type="date" value={modal.incident_date} onChange={e => setModal(m => ({ ...m, incident_date:e.target.value }))} /></div>
              <div className="form-group"><label>Incident Time</label>
                <input type="time" value={modal.incident_time} onChange={e => setModal(m => ({ ...m, incident_time:e.target.value }))} /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>Campus *</label>
                <select value={modal.campus_id} disabled={!!modal.id}
                  onChange={e => setModal(m => ({ ...m, campus_id:e.target.value, location_id:'', primary_camera_id:'', staff_ids:[], student_ids:[], camera_ids:[] }))}>
                  <option value="">— Select —</option>
                  {campuses.filter(c => seesAll || (user.campus_ids||[]).includes(c.id)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
              <div className="form-group"><label>Building</label>
                <input value={modal.building} onChange={e => setModal(m => ({ ...m, building:e.target.value }))} /></div>
              <div className="form-group"><label>Location</label>
                <select value={modal.location_id} onChange={e => setModal(m => ({ ...m, location_id:e.target.value }))}>
                  <option value="">— Select —</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select></div>
            </div>
            <div className="form-group"><label>Purpose</label>
              <textarea rows={2} value={modal.purpose} onChange={e => setModal(m => ({ ...m, purpose:e.target.value }))} /></div>

            <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', margin:'10px 0 6px' }}>PERSONS INVOLVED</div>
            <div className="flex-gap" style={{ gap:16, marginBottom:8, flexWrap:'wrap' }}>
              {[['involves_staff','Staff'],['involves_student','Student'],['involves_outside','Outside Person']].map(([k,l]) => (
                <label className="opt" key={k} style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:13 }}>
                  <input type="checkbox" checked={!!modal[k]} onChange={e => setModal(m => ({ ...m, [k]:e.target.checked }))} /> {l}
                </label>
              ))}
            </div>
            {modal.involves_staff && (
              <div className="form-group">
                <label>Staff <span style={{ fontSize:11, color:'var(--text2)' }}>({modal.staff_ids.length} selected)</span></label>
                <input placeholder="Search staff…" value={staffQ} onChange={e => setStaffQ(e.target.value)} style={{ marginBottom:6 }} />
                <div style={{ maxHeight:130, overflowY:'auto', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'6px 10px' }}>
                  {staffList.filter(s => !staffQ || s.name.toLowerCase().includes(staffQ.toLowerCase())).slice(0,60).map(s => (
                    <label className="opt" key={s.id} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, padding:'2px 0' }}>
                      <input type="checkbox" checked={modal.staff_ids.includes(s.id)} onChange={() => toggle('staff_ids', s.id)} />
                      {s.name}{s.designation && <span style={{ color:'var(--text2)', fontSize:11 }}>· {s.designation}</span>}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {modal.involves_student && (
              <div className="form-group">
                <label>Students <span style={{ fontSize:11, color:'var(--text2)' }}>({modal.student_ids.length} selected)</span></label>
                <input placeholder="Search name or ID…" value={studentQ} onChange={e => setStudentQ(e.target.value)} style={{ marginBottom:6 }} />
                <div style={{ maxHeight:130, overflowY:'auto', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'6px 10px' }}>
                  {studentList.filter(s => !studentQ || s.name.toLowerCase().includes(studentQ.toLowerCase())
                      || (s.id_number||'').toLowerCase().includes(studentQ.toLowerCase())).slice(0,60).map(s => (
                    <label className="opt" key={s.id} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, padding:'2px 0' }}>
                      <input type="checkbox" checked={modal.student_ids.includes(s.id)} onChange={() => toggle('student_ids', s.id)} />
                      {s.name}<span style={{ color:'var(--text2)', fontSize:11 }}>
                        {s.id_number ? `· ${s.id_number}` : ''}{s.grade ? ` · Gr ${s.grade}${s.section||''}` : ''}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {modal.involves_outside && (
              <div className="form-group"><label>Outside Person Details</label>
                <textarea rows={2} value={modal.outside_details} onChange={e => setModal(m => ({ ...m, outside_details:e.target.value }))} /></div>
            )}

            <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', margin:'10px 0 6px' }}>CAMERA COVERAGE</div>
            <div className="flex-gap" style={{ gap:8, marginBottom:8 }}>
              <button className={`btn btn-sm ${modal.camera_covers===true?'btn-primary':'btn-secondary'}`}
                onClick={() => setModal(m => ({ ...m, camera_covers:true }))}>Camera covers incident — YES</button>
              <button className={`btn btn-sm ${modal.camera_covers===false?'btn-danger':'btn-secondary'}`}
                onClick={() => setModal(m => ({ ...m, camera_covers:false }))}>NO</button>
            </div>
            {modal.camera_covers === true && (<>
              <div className="form-group"><label>Primary Camera</label>
                <select value={modal.primary_camera_id} onChange={e => setModal(m => ({ ...m, primary_camera_id:e.target.value }))}>
                  <option value="">— Select —</option>
                  {cameras.map(c => <option key={c.id} value={c.id}>
                    {c.camera_number ? `#${c.camera_number} · ` : ''}{c.camera_name || c.asset_tag}{c.location_name ? ` (${c.location_name})` : ''}
                  </option>)}
                </select></div>
              <div className="form-group"><label>Additional Cameras <span style={{ fontSize:11, color:'var(--text2)' }}>({modal.camera_ids.length} selected)</span></label>
                <div style={{ maxHeight:120, overflowY:'auto', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'6px 10px' }}>
                  {cameras.filter(c => String(c.id) !== String(modal.primary_camera_id)).map(c => (
                    <label className="opt" key={c.id} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, padding:'2px 0' }}>
                      <input type="checkbox" checked={modal.camera_ids.includes(c.id)} onChange={() => toggle('camera_ids', c.id)} />
                      {c.camera_number ? `#${c.camera_number} · ` : ''}{c.camera_name || c.asset_tag}
                    </label>
                  ))}
                </div></div>
            </>)}
            {modal.camera_covers === false && (
              <div className="form-group"><label>Reason *</label>
                <select value={modal.no_coverage_reason} onChange={e => setModal(m => ({ ...m, no_coverage_reason:e.target.value }))}>
                  <option value="">— Select —</option>{NO_COVER.map(r => <option key={r}>{r}</option>)}
                </select></div>
            )}

            <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', margin:'10px 0 6px' }}>VERIFICATION RESULT</div>
            <div className="form-row">
              <div className="form-group"><label>Footage Available</label>
                <select value={modal.footage_available === null ? '' : String(modal.footage_available)}
                  onChange={e => setModal(m => ({ ...m, footage_available: e.target.value === '' ? null : e.target.value === 'true' }))}>
                  <option value="">—</option><option value="true">Yes</option><option value="false">No</option>
                </select></div>
              <div className="form-group"><label>Recording Quality</label>
                <select value={modal.recording_quality} onChange={e => setModal(m => ({ ...m, recording_quality:e.target.value }))}>
                  <option value="">—</option><option>Good</option><option>Average</option><option>Poor</option><option>Not Available</option>
                </select></div>
              <div className="form-group"><label>Verification Date</label>
                <input type="date" value={modal.verification_date} onChange={e => setModal(m => ({ ...m, verification_date:e.target.value }))} /></div>
              <div className="form-group"><label>Status</label>
                <select value={modal.status} onChange={e => setModal(m => ({ ...m, status:e.target.value }))}>
                  {FR_STATUS.map(s => <option key={s}>{s}</option>)}
                </select></div>
            </div>
            <div className="form-group"><label>Remarks</label>
              <textarea rows={2} value={modal.remarks} onChange={e => setModal(m => ({ ...m, remarks:e.target.value }))} /></div>

            {err && <div className="err-msg">{err}</div>}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>{busy?'Saving…':'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Dashboard ──────────────────────────────────────────────────────────────
function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get('/surveillance/stats').then(r => setD(r.data)).catch(()=>{}); }, []);
  if (!d) return <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>Loading…</div>;
  const Stat = ({ label, value, color }) => (
    <div className="card" style={{ flex:1, minWidth:150 }}>
      <div style={{ fontSize:12, color:'var(--text2)', marginBottom:6 }}>{label}</div>
      <div style={{ fontSize:26, fontWeight:700, color:color||'var(--text)' }}>{value ?? 0}</div>
    </div>
  );
  return (
    <div>
      <div className="flex-gap" style={{ gap:12, flexWrap:'wrap', marginBottom:16 }}>
        <Stat label="Total DVRs" value={d.totals.total_dvrs} color="var(--accent)" />
        <Stat label="Healthy today" value={d.today.healthy_dvrs} color="var(--green)" />
        <Stat label="Faulty" value={d.today.faulty_dvrs} color="var(--amber)" />
        <Stat label="Offline" value={d.today.offline_dvrs} color="var(--red)" />
        <Stat label="Pending checks" value={d.totals.pending_checks} color="var(--orange)" />
      </div>
      <div className="flex-gap" style={{ gap:12, flexWrap:'wrap', marginBottom:20 }}>
        <Stat label="Total Cameras" value={d.totals.total_cameras} color="var(--accent)" />
        <Stat label="Working (today)" value={d.today.cameras_working} color="var(--green)" />
        <Stat label="Faulty (today)" value={d.today.cameras_faulty} color="var(--red)" />
        <Stat label="Playback issues today" value={d.today.playback_issues} color="var(--red)" />
      </div>

      <h3 style={{ fontSize:14, fontWeight:600, marginBottom:8 }}>Campus summary</h3>
      <div className="card" style={{ padding:0, overflow:'hidden', marginBottom:20 }}>
        <div className="table-wrap"><table>
          <thead><tr><th>Campus</th><th>DVRs</th><th>Cameras</th><th>Checked today</th><th>Pending</th></tr></thead>
          <tbody>{d.byCampus.map(c => (
            <tr key={c.id}>
              <td style={{ fontWeight:500 }}>{c.campus}</td><td>{c.dvrs}</td><td>{c.cameras}</td>
              <td style={{ color:'var(--green)' }}>{c.checked_today}</td>
              <td style={{ color: c.dvrs - c.checked_today > 0 ? 'var(--orange)' : 'var(--text2)' }}>{Math.max(0, c.dvrs - c.checked_today)}</td>
            </tr>
          ))}</tbody>
        </table></div>
      </div>

      <h3 style={{ fontSize:14, fontWeight:600, marginBottom:8 }}>Playback retention</h3>
      <div className="card" style={{ padding:0, overflow:'hidden', marginBottom:20 }}>
        <div className="table-wrap"><table>
          <thead><tr><th>DVR</th><th>Campus</th><th>Hard disk</th><th>Oldest recording</th><th>Retention</th><th>Expected</th><th>Last verified</th></tr></thead>
          <tbody>{d.retention.map(r => {
            const short = r.retention_days != null && r.retention_days < expectedDays(r);
            return (
              <tr key={r.id}>
                <td style={{ fontWeight:500 }}>{r.dvr_name || r.asset_tag}</td>
                <td style={{ fontSize:12 }}>{r.campus_name}</td>
                <td style={{ fontSize:12 }}>{r.hdd_capacity_tb ? `${r.hdd_capacity_tb} TB` : '—'}</td>
                <td style={{ fontSize:12 }}>{fmt(r.oldest_recording_date)}</td>
                <td style={{ fontWeight:600, color: short ? 'var(--orange)' : 'var(--text)' }}>
                  {r.retention_days != null ? `${r.retention_days}d` : '—'}{short && ' 🟠'}
                </td>
                <td style={{ fontSize:12 }}>{expectedDays(r)}d</td>
                <td style={{ fontSize:12 }}>{fmt(r.last_verified)}</td>
              </tr>
            );
          })}</tbody>
        </table></div>
      </div>

      <h3 style={{ fontSize:14, fontWeight:600, marginBottom:8 }}>Recent faults</h3>
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!d.recent.length ? <div className="empty-state"><p>No faults recorded.</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Date</th><th>DVR</th><th>Campus</th><th>Status</th><th>Reason</th></tr></thead>
            <tbody>{d.recent.map((r, i) => (
              <tr key={i}>
                <td style={{ fontSize:12 }}>{fmt(r.check_date)}</td>
                <td style={{ fontWeight:500 }}>{r.dvr_name || r.asset_tag}</td>
                <td style={{ fontSize:12 }}>{r.campus_name}</td>
                <td>{pill(r.overall_status)}</td>
                <td style={{ fontSize:12, color:'var(--text2)' }}>{r.issue_reason || r.issue_desc || '—'}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
